// src/utils/wordScoring.js

/**
 * ─── Word Scoring Engine ─────────────────────────────────────────
 *
 * ALIGNMENT STRATEGY:
 *
 *  For each typed word we ask:
 *
 *  Q1. Does it match originalTokens[origIndex] exactly?
 *      YES → correct, advance origIndex.
 *            If slot was already "incorrect" (retry), upgrade it.
 *            wrongWords already counted for the earlier failed attempt.
 *
 *  Q2. Does it match any original word within LOOKAHEAD slots ahead?
 *      YES → slots in between = missed, matched slot = correct,
 *            origIndex jumps past it.
 *
 *  Q3. Can consecutive typed tokens be merged to form originalTokens[origIndex]?
 *      YES → split-word detected, count as 1 wrong, consume merged tokens,
 *            advance origIndex.
 *
 *  Q4. None of the above → wrong word.
 *      Record the wrong attempt (always).
 *      Then peek at the NEXT typed word:
 *        - If next typed word == originalTokens[origIndex] → keep origIndex
 *          so next iteration can upgrade this slot to correct (retry).
 *        - Otherwise → advance origIndex (user moved on).
 *
 * RETRY DISPLAY:
 *  When a slot is retried successfully, we want to show BOTH the wrong
 *  attempt AND the correct word. So wordTypedAs stores:
 *    - typedWord : the FIRST wrong attempt (shown as strikethrough)
 *    - attempts  : ALL attempts including the final correct one
 *  The WordReplay component uses attempts[0] as the wrong display
 *  and original as the correct display.
 *
 * SPLIT-WORD:
 *  Test mode has no backspace. Users sometimes hit space mid-word,
 *  splitting "the" → "th" + "e". Detected by merging consecutive
 *  typed tokens and checking against current original word.
 *
 * FORMULA:
 *  accuracy = rightWords / (rightWords + wrongWords) × 100
 *  Missed and Pending do NOT count toward accuracy.
 */

const LOOKAHEAD = 3;

// ─── Tokenize ─────────────────────────────────────────────────────
export const tokenizeWords = (text) => {
  const tokens = [];
  const regex  = /\S+/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    tokens.push({
      word:       match[0],
      startIndex: match.index,
      endIndex:   match.index + match[0].length - 1,
    });
  }
  return tokens;
};

// ─── Per-character status builder ─────────────────────────────────
const buildCharStatuses = (originalText, originalTokens, wordStatuses) => {
  const len      = originalText.length;
  const statuses = new Array(len).fill("pending");

  originalTokens.forEach((token, wordIdx) => {
    const status = wordStatuses[wordIdx];
    for (let i = token.startIndex; i <= token.endIndex; i++) {
      statuses[i] = status;
    }
  });

  for (let i = 0; i < len; i++) {
    if (statuses[i] === "pending" && /\s/.test(originalText[i])) {
      let prev = i - 1;
      while (prev >= 0 && /\s/.test(originalText[prev])) prev--;
      if (prev >= 0 && statuses[prev] !== "pending") {
        statuses[i] = statuses[prev];
      }
    }
  }

  return statuses;
};

// ─── Split-word check ─────────────────────────────────────────────
/**
 * Check if typedTokens[fromIndex..fromIndex+n] concatenated
 * equals originalWord. Returns tokens consumed count or -1.
 */
const checkSplitWord = (originalWord, typedTokens, fromIndex, maxMerge = 4) => {
  let merged = "";
  for (
    let i = fromIndex;
    i < Math.min(fromIndex + maxMerge, typedTokens.length);
    i++
  ) {
    merged += typedTokens[i].word;
    if (merged === originalWord) {
      return i - fromIndex + 1;
    }
    if (merged.length >= originalWord.length) break;
  }
  return -1;
};

// ─── Find lookahead match ─────────────────────────────────────────
const findLookaheadMatch = (typedWord, originalTokens, fromIndex) => {
  for (
    let ahead = fromIndex + 1;
    ahead < Math.min(fromIndex + LOOKAHEAD + 1, originalTokens.length);
    ahead++
  ) {
    if (originalTokens[ahead].word === typedWord) return ahead;
  }
  return -1;
};

// ─── Core alignment ───────────────────────────────────────────────
const alignWords = (originalTokens, typedTokens) => {
  const totalWordCount = originalTokens.length;

  const wordStatuses = new Array(totalWordCount).fill("pending");

  // wordTypedAs[i]:
  //   firstWrong : the very first wrong attempt (for display)
  //   typedWord  : final resolved word shown in chip
  //   attempts   : every attempt in order
  const wordTypedAs = Array.from({ length: totalWordCount }, () => ({
    firstWrong: null,   // ← NEW: stores first wrong attempt
    typedWord:  null,
    attempts:   [],
  }));

  let origIndex    = 0;
  let rightWords   = 0;
  let wrongWords   = 0;
  let missedWords  = 0;
  let extraWords   = 0;
  const extraWordsList = [];

  let t = 0;

  while (t < typedTokens.length) {
    const typedWord = typedTokens[t].word;

    // ── Beyond end of original ──
    if (origIndex >= totalWordCount) {
      extraWords++;
      wrongWords++;
      extraWordsList.push(typedWord);
      t++;
      continue;
    }

    const originalWord = originalTokens[origIndex].word;

    // ──────────────────────────────────────────────────────────
    // CASE 1: Exact match at current slot
    // ──────────────────────────────────────────────────────────
    if (typedWord === originalWord) {
      if (wordStatuses[origIndex] === "incorrect") {
        // ── Retry succeeded ──
        // wrongWords already counted for the failed attempt(s).
        // Upgrade slot to correct.
        rightWords++;
        // Keep firstWrong so WordReplay can show "proc → process"
        // Just update typedWord to the correct final word
        wordTypedAs[origIndex].typedWord = typedWord;
        wordTypedAs[origIndex].attempts.push(typedWord);
      } else {
        rightWords++;
        wordTypedAs[origIndex].typedWord = typedWord;
        wordTypedAs[origIndex].attempts.push(typedWord);
      }
      wordStatuses[origIndex] = "correct";
      origIndex++;
      t++;
      continue;
    }

    // ──────────────────────────────────────────────────────────
    // CASE 2: Lookahead — typed word matches an original word ahead
    // ──────────────────────────────────────────────────────────
    const lookaheadMatch = findLookaheadMatch(
      typedWord, originalTokens, origIndex
    );

    if (lookaheadMatch !== -1) {
      for (let skipped = origIndex; skipped < lookaheadMatch; skipped++) {
        if (wordStatuses[skipped] === "pending") {
          wordStatuses[skipped] = "missed";
          missedWords++;
        }
      }
      wordStatuses[lookaheadMatch]          = "correct";
      wordTypedAs[lookaheadMatch].typedWord = typedWord;
      wordTypedAs[lookaheadMatch].attempts.push(typedWord);
      rightWords++;
      origIndex = lookaheadMatch + 1;
      t++;
      continue;
    }

    // ──────────────────────────────────────────────────────────
    // CASE 3: Split-word
    // ──────────────────────────────────────────────────────────
    const splitConsumed = checkSplitWord(originalWord, typedTokens, t);
    if (splitConsumed > 1) {
      const mergedTyped = typedTokens
        .slice(t, t + splitConsumed)
        .map((tk) => tk.word)
        .join(" ");

      wordStatuses[origIndex]             = "incorrect";
      wordTypedAs[origIndex].firstWrong   = mergedTyped;
      wordTypedAs[origIndex].typedWord    = mergedTyped;
      wordTypedAs[origIndex].attempts.push(mergedTyped);
      wrongWords++;
      origIndex++;
      t += splitConsumed;
      continue;
    }

    // ──────────────────────────────────────────────────────────
    // CASE 4: Wrong word — no match anywhere
    //
    // ALWAYS record the wrong attempt and count it.
    // Then decide whether to advance origIndex or keep it for retry.
    // ──────────────────────────────────────────────────────────
    if (wordStatuses[origIndex] !== "correct") {
      wordStatuses[origIndex] = "incorrect";

      // ── Record firstWrong only on the very first failed attempt ──
      if (wordTypedAs[origIndex].firstWrong === null) {
        wordTypedAs[origIndex].firstWrong = typedWord; // ← always saved
      }
      wordTypedAs[origIndex].typedWord = typedWord;
    }
    wordTypedAs[origIndex].attempts.push(typedWord);

    // Always count this as a wrong word
    wrongWords++; // ← always counted, even if retry will follow

    // ── Peek: will the next typed word retry this same slot? ──
    const nextTypedWord = typedTokens[t + 1]?.word;

    if (
      nextTypedWord !== undefined &&
      nextTypedWord === originalTokens[origIndex].word
    ) {
      // Next word is the correct retry → keep origIndex
      // origIndex stays so next iteration upgrades this slot
    } else {
      // User moved on → advance origIndex
      origIndex++;
    }

    t++;
  }

  return {
    wordStatuses,
    wordTypedAs,
    rightWords,
    wrongWords,
    missedWords,
    extraWords,
    extraWordsList,
  };
};

// ─── Main scoring function ────────────────────────────────────────
export const scoreWords = (originalText, typedText, isFinished = false) => {
  const originalTokens = tokenizeWords(originalText);
  const typedTokens    = tokenizeWords(typedText);
  const totalWordCount = originalTokens.length;

  const {
    wordStatuses,
    wordTypedAs,
    rightWords,
    wrongWords,
    missedWords,
    extraWords,
    extraWordsList,
  } = alignWords(originalTokens, typedTokens);

  const pendingWords   = wordStatuses.filter((s) => s === "pending").length;
  const typedWordCount = rightWords + wrongWords;
  const accuracy =
    typedWordCount > 0
      ? parseFloat(((rightWords / typedWordCount) * 100).toFixed(3))
      : 100;

  const charStatuses = buildCharStatuses(
    originalText,
    originalTokens,
    wordStatuses
  );

  // ── Rich per-word result list for visual replay ──
  const wordResults = originalTokens.map((token, i) => ({
    original:   token.word,
    status:     wordStatuses[i],
    typedWord:  wordTypedAs[i].typedWord,
    firstWrong: wordTypedAs[i].firstWrong, // ← NEW
    attempts:   wordTypedAs[i].attempts,
  }));

  extraWordsList.forEach((w) => {
    wordResults.push({
      original:   null,
      status:     "extra",
      typedWord:  w,
      firstWrong: null,
      attempts:   [w],
    });
  });

  return {
    rightWords,
    wrongWords,
    missedWords,
    pendingWords,
    extraWords,
    typedWordCount,
    totalWordCount,
    accuracy,
    wordStatuses,
    charStatuses,
    wordResults,
  };
};

// ─── Accuracy label helper ────────────────────────────────────────
export const getWordAccuracyLabel = (accuracy) => {
  if (accuracy >= 99) return { label: "Flawless! 🌟",       color: "#28a745" };
  if (accuracy >= 95) return { label: "Excellent! ✨",       color: "#28a745" };
  if (accuracy >= 85) return { label: "Great job! 👍",       color: "#667eea" };
  if (accuracy >= 70) return { label: "Keep practicing! 💪", color: "#ffc107" };
  return               { label: "Focus on accuracy! 🎯",     color: "#dc3545" };
};